/**
 * PasadiasLayout — Sidebar secundario de Pasadías + outlet.
 * Mismo patrón que CatalogoLayout para consistencia visual.
 * Sub-secciones: Calendario, Salidas, Generar, Asignar botes, Clima, Histórico.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Chip, Tabs, Tab, Badge } from '@mui/material';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import CloudIcon from '@mui/icons-material/Cloud';
import HistoryIcon from '@mui/icons-material/History';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; }
  catch { return '1'; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const SECTIONS = [
  { path: 'calendario',  label: 'Calendario',       icon: <CalendarMonthIcon />, color: '#0ea5e9' },
  { path: 'salidas',     label: 'Salidas',          icon: <EventNoteIcon />,     color: '#0d9488' },
  { path: 'generar',     label: 'Generar masivas',  icon: <EventRepeatIcon />,   color: '#8b5cf6' },
  { path: 'asignar',     label: 'Asignar botes',    icon: <DirectionsBoatIcon />,color: '#f59e0b' },
  { path: 'clima',       label: 'Clima',            icon: <CloudIcon />,         color: '#06b6d4' },
  { path: 'historico',   label: 'Histórico',        icon: <HistoryIcon />,       color: '#94a3b8' },
];

export default function PasadiasLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const empresaId = getEmpresaId();
  const [counts, setCounts] = useState({});

  const cargarCounts = useCallback(async () => {
    try {
      // Próximas salidas (hoy en adelante) → para Calendario/Salidas
      const r = await fetch(
        `${API_BASE}/turismo/salidas?empresa_id=${empresaId}`,
        { headers: getAuthHeaders() }
      );
      if (r.ok) {
        const data = await r.json();
        const lista = Array.isArray(data) ? data : (data.salidas || []);
        const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
        const futuras = lista.filter(s => {
          const f = s.fecha ? new Date(s.fecha) : null;
          return f && f >= hoy && s.estado !== 'cancelada' && s.estado !== 'completada';
        });
        const pasadas = lista.length - futuras.length;
        const sinBotes = futuras.filter(s => !s.botes || s.botes.length === 0).length;
        const climaAlerta = futuras.filter(s => s.estado_clima && s.estado_clima !== 'normal').length;

        setCounts({
          calendario: { total: futuras.length },
          salidas:    { total: futuras.length },
          asignar:    { total: sinBotes, warn: sinBotes > 0 },
          clima:      { total: climaAlerta, warn: climaAlerta > 0 },
          historico:  { total: pasadas },
        });
      }
    } catch (e) { console.error(e); }
  }, [empresaId]);

  useEffect(() => { cargarCounts(); }, [cargarCounts]);

  // Redirigir a "salidas" si entran a /pasadias sin sub-ruta
  useEffect(() => {
    if (location.pathname === '/pasadias' || location.pathname === '/pasadias/') {
      navigate('/pasadias/salidas', { replace: true });
    }
    // También redirigir desde /salidas legacy
    if (location.pathname === '/salidas') {
      navigate('/pasadias/salidas', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
      {/* Sidebar secundario */}
      <Box sx={{
        width: 240, flexShrink: 0, bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
      }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
            Pasadías
          </Typography>
        </Box>
        <List sx={{ px: 1, py: 0 }} dense>
          {SECTIONS.map(s => {
            const isActive = location.pathname.includes(`/pasadias/${s.path}`);
            const c = counts[s.path];
            return (
              <ListItemButton key={s.path} onClick={() => navigate(`/pasadias/${s.path}`)}
                sx={{
                  borderRadius: 2, mb: 0.5, py: 0.75, pl: 1.5,
                  bgcolor: isActive ? `${s.color}15` : 'transparent',
                  borderLeft: isActive ? `3px solid ${s.color}` : '3px solid transparent',
                  '&:hover': { bgcolor: isActive ? `${s.color}25` : '#f1f5f9' },
                }}>
                <ListItemIcon sx={{ minWidth: 32, color: isActive ? s.color : '#94a3b8' }}>
                  {s.icon}
                </ListItemIcon>
                <ListItemText primary={s.label}
                  primaryTypographyProps={{
                    fontSize: 13, fontWeight: isActive ? 700 : 500,
                    color: isActive ? '#0f172a' : '#475569',
                  }} />
                {c && c.total > 0 && (
                  <Chip size="small" label={c.total}
                    sx={{ height: 18, fontSize: 10, fontWeight: 700,
                          bgcolor: c.warn ? '#fef3c7' : (isActive ? s.color : '#e2e8f0'),
                          color: c.warn ? '#92400e' : (isActive ? '#fff' : '#64748b') }} />
                )}
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ flex: 1 }} />
        <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.5 }}>
            Gestiona las salidas programadas, la asignación de botes y el estado climático
            de cada pasadía.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Tabs móviles — sólo visibles en xs/sm. Reemplazan el sidebar oculto. */}
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          mb: 2, mx: -2, px: 2,
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
          position: 'sticky', top: -16, zIndex: 5,
        }}>
          <Tabs
            value={Math.max(0, SECTIONS.findIndex(s => location.pathname.includes(`/pasadias/${s.path}`)))}
            onChange={(_, idx) => navigate(`/pasadias/${SECTIONS[idx].path}`)}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={{
              minHeight: 44,
              '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 700, fontSize: 12, px: 1.5 },
              '& .MuiTabs-indicator': { backgroundColor: '#0d9488', height: 3, borderRadius: 2 },
            }}
          >
            {SECTIONS.map(s => {
              const c = counts[s.path];
              const n = c?.total || 0;
              return (
                <Tab key={s.path}
                  icon={s.icon} iconPosition="start"
                  label={
                    <Badge badgeContent={n} max={99}
                      sx={{
                        '& .MuiBadge-badge': {
                          bgcolor: c?.warn ? '#f59e0b' : s.color, color: '#fff',
                          fontSize: 9, height: 16, minWidth: 16, right: -6, top: 2,
                        },
                      }}>
                      <span style={{ paddingRight: n > 0 ? 10 : 0 }}>{s.label}</span>
                    </Badge>
                  }
                />
              );
            })}
          </Tabs>
        </Box>

        <Outlet context={{ refreshCounts: cargarCounts }} />
      </Box>
    </Box>
  );
}
